import { useState, useEffect, useRef } from 'react';
import { Heart, ChevronUp } from 'lucide-react';
import { FOOTER, MY_NAME } from '@/config/valentineContent';

export default function ValentineFooter() {
  const [easterEgg, setEasterEgg] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const typingRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!easterEgg) return;
    const text = FOOTER.easterEggMessages[msgIndex];
    setTyped('');
    let i = 0;
    typingRef.current = setInterval(() => {
      i++;
      setTyped(text.slice(0, i));
      if (i >= text.length) clearInterval(typingRef.current);
    }, 35);
    return () => { if (typingRef.current) clearInterval(typingRef.current); };
  }, [easterEgg, msgIndex]);

  const handleEasterEgg = () => {
    if (easterEgg) setMsgIndex(prev => (prev + 1) % FOOTER.easterEggMessages.length);
    else setEasterEgg(true);
  };

  return (
    <footer data-testid="footer-section" className="relative py-16 text-center overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent, var(--accent-light), transparent)' }} />

      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="font-body text-xs" style={{ color: 'var(--text-secondary)' }}>Made with</span>
        <Heart size={12} fill="var(--accent-primary)" strokeWidth={0} className="footer-heart-beat" />
        <span className="font-body text-xs" style={{ color: 'var(--text-secondary)' }}>by {MY_NAME}</span>
      </div>

      <p className="font-body text-[10px] mb-5" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>{FOOTER.subtitle}</p>

      <button data-testid="easter-egg-btn" onClick={handleEasterEgg}
        className="footer-psst font-body text-[10px] transition-all duration-500 hover:opacity-80"
        style={{ color: 'var(--accent-light)', opacity: easterEgg ? 0.8 : 0.3 }}>
        {easterEgg ? FOOTER.easterEggMore : FOOTER.easterEggTrigger}
      </button>

      {easterEgg && (
        <div className="footer-egg-reveal mt-4 max-w-xs mx-auto">
          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--accent-glow)', border: '1px solid var(--accent-light)' }}>
            <p className="font-body text-xs leading-relaxed" style={{ color: 'var(--accent-primary)' }}>
              {typed}<span className="footer-cursor">|</span>
            </p>
          </div>
        </div>
      )}

      {showScrollTop && (
        <button data-testid="scroll-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="footer-scroll-top fixed bottom-6 right-6 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
          style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent-light)', boxShadow: '0 4px 16px var(--accent-glow)', color: 'var(--accent-primary)' }}>
          <ChevronUp size={18} />
        </button>
      )}

      <p className="font-body text-[10px] mt-8" style={{ color: 'var(--text-secondary)', opacity: 0.3 }}>{new Date().getFullYear()}</p>
    </footer>
  );
}
